import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import { getCareflowToken } from 'src/careflow/ph-core/authentication/util/token';

export const ICOMX_LOGOUT_EVENT = 'careflow:icomx-logout';
export const ICOMX_LOGOUT_STORAGE_KEY = 'icomx-logged-out-session';
export const ICOMX_ACCESS_TOKEN_HEADER = 'X-Careflow-Access-Token';

interface SessionClaims {
  sub?: string;
  user_uid?: string;
  tenant_code?: string;
  organization?: string;
  role?: string | number;
  session_id?: string;
  exp?: number;
}

export interface IcomxSession {
  key: string;
  logoutKey: string;
  expiresAt: number;
}

const hasText = (value: unknown): value is string => typeof value === 'string' && !!value.trim();

// Unlike the shared availability helper, this requires the post-selection token
// and matching upstream credentials. Optional display names/FHIR URIs are not login requirements.
export function readIcomxSession(): IcomxSession | null {
  try {
    const token = getCareflowToken(true);
    const accessToken = Cookies.get('accessCode');
    if (!token || !accessToken) return null;
    const claims = jwtDecode<SessionClaims>(token);
    const access = jwtDecode<SessionClaims>(accessToken);
    const userId = claims.user_uid;
    const role = Number(claims.role);
    const expiresAt = Math.min(Number(claims.exp), Number(access.exp)) * 1000;
    if (
      !hasText(userId) || !hasText(claims.sub) || userId !== claims.sub ||
      !hasText(claims.tenant_code) || !hasText(claims.organization) ||
      (typeof claims.role !== 'string' && typeof claims.role !== 'number') ||
      !Number.isSafeInteger(role) || role <= 0 ||
      access.sub !== claims.sub || access.exp !== claims.exp || !Number.isFinite(expiresAt) || expiresAt <= Date.now()
    ) return null;

    const logoutKey = JSON.stringify([claims.tenant_code, userId, claims.session_id ?? '']);
    return {
      key: JSON.stringify([logoutKey, claims.organization, role]),
      logoutKey,
      expiresAt,
    };
  } catch (_error) {
    return null;
  }
}

export function isIcomxSessionLoggedOut(session: IcomxSession): boolean {
  try {
    return localStorage.getItem(ICOMX_LOGOUT_STORAGE_KEY) === session.logoutKey;
  } catch (_error) {
    return false;
  }
}

// Only invalidates the embedded chat. Careflow's existing logout owns its credentials.
export function notifyIcomxLogout() {
  const session = readIcomxSession();
  try {
    if (session) localStorage.setItem(ICOMX_LOGOUT_STORAGE_KEY, session.logoutKey);
  } catch (_error) {
    // The same-tab event still tears down chat when browser storage is unavailable.
  }
  window.dispatchEvent(new Event(ICOMX_LOGOUT_EVENT));
}
